import { useCallback, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';
import { isBadRequestError } from '@/core/api/badRequestError';
import { uploadImage } from '@/entities/Media/api/imageService';
import { orderService } from '@/entities/Order/api/orderService';
import { completeOrderFormToRequest } from '@/entities/Order/completeOrderMappers';
import type { CompleteOrderFormValues } from '@/entities/Order/model/completeOrderSchema';
import { getErrorMessage } from '@/shared/lib/errors';

/** Загружает фотографии отчёта и завершает заказ */
export const useCompleteOrderSubmit = (
    orderId: string,
    form: UseFormReturn<CompleteOrderFormValues>,
    onCompleted: () => void,
) => {
    const uploadedPhotos = useRef(new Map<File, string>());
    const submitting = useRef(false);
    const [progress, setProgress] = useState('Завершаем заказ…');

    /** Очищает фотографии предыдущего отчёта */
    const resetUploads = useCallback(() => uploadedPhotos.current.clear(), []);

    /** Отправляет отчёт о выполнении заказа */
    const onSubmit = async (values: CompleteOrderFormValues) => {
        if (submitting.current) return;

        submitting.current = true;
        form.clearErrors('root.server');

        try {
            const photoUrls: string[] = [];

            for (const [index, file] of values.photos.entries()) {
                setProgress(`Загружаем фотографии: ${index + 1} из ${values.photos.length}…`);
                const url = uploadedPhotos.current.get(file) ?? await uploadImage(file);
                uploadedPhotos.current.set(file, url);
                photoUrls.push(url);
            }

            setProgress('Завершаем заказ…');
            await orderService.completeOrder(completeOrderFormToRequest(values, orderId, photoUrls));
            toast.success('Заказ успешно завершён');
            onCompleted();
        } catch (error: unknown) {
            if (!isBadRequestError(error)) {
                form.setError('root.server', {
                    type: 'server',
                    message: getErrorMessage(error, 'Не удалось завершить заказ'),
                });
            }
        } finally {
            submitting.current = false;
        }
    };

    return {
        onSubmit,
        progress,
        resetUploads
    };
};
